import { useEffect, useState, useCallback } from "react";
import AppShell from "@/components/AppShell";
import MathText from "@/components/MathText";
import TalentaCTA from "@/components/TalentaCTA";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Clock, ArrowLeft, ArrowRight, Trophy, CheckCircle2, XCircle, RotateCcw, ExternalLink, Lock, Crown, Flag, GraduationCap, TrendingUp, TrendingDown, Minus, Target } from "lucide-react";
import TargetProdiModal from "@/components/TargetProdiModal";
import { ScoreTrendChart } from "@/components/ScoreTrendChart";
import MotivasiQuote from "@/components/MotivasiQuote";

const rupiah = (n) => "Rp" + (n || 0).toLocaleString("id-ID");

export default function TryOut() {
  const [list, setList] = useState([]);
  const [eksternal, setEksternal] = useState([]);
  const [active, setActive] = useState(null);
  const [sections, setSections] = useState([]);
  const [secIdx, setSecIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [buying, setBuying] = useState(null);
  const [picker, setPicker] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [reportFor, setReportFor] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [reportSending, setReportSending] = useState(false);
  const [prodiTargets, setProdiTargets] = useState([]);
  const [targetModal, setTargetModal] = useState(false);

  const submitReport = async () => {
    if (!reportReason.trim()) { toast.error("Tulis dulu masalah pada soal ini"); return; }
    setReportSending(true);
    try {
      await api.post("/tryout/report-question", { tryout_id: active?.id, question_id: reportFor?.id, question_text: reportFor?.question, reason: reportReason.trim() });
      toast.success("Terima kasih! Laporan soal terkirim ke admin.");
      setReportFor(null); setReportReason("");
    } catch { toast.error("Gagal mengirim laporan"); }
    finally { setReportSending(false); }
  };

  const buyTryout = async (tid, tier) => {
    setBuying(`${tid}:${tier}`);
    try {
      const { data } = await api.post("/payments/tryout/snap", { tryout_id: tid, tier, origin_url: window.location.origin });
      window.location.href = data.redirect_url;
    } catch { toast.error("Gagal memulai pembayaran"); setBuying(null); }
  };

  const loadList = () => {
    api.get("/tryout/list").then((r) => { setList(r.data.tryouts); setEksternal(r.data.eksternal || []); }).catch(() => {});
    api.get("/tryout/results/all").then((r) => setHistory(r.data.results)).catch(() => {});
    api.get("/student/prodi").then((r) => setProdiTargets(r.data.items || [])).catch(() => {});
  };
  useEffect(loadList, []);

  const buildSections = (t) => {
    if (t.subtes_meta && t.subtes_meta.length) {
      return t.subtes_meta.map((m) => ({ nama: m.nama, durasi: m.durasi, questions: t.soal.filter((q) => q.subtes === m.nama) }));
    }
    return [{ nama: null, durasi: t.durasi_menit || 30, questions: t.soal }];
  };

  const begin = async (id, pilihanArr) => {
    const qs = pilihanArr && pilihanArr.length ? `?pilihan=${encodeURIComponent(pilihanArr.join(","))}` : "";
    const { data } = await api.get(`/tryout/${id}${qs}`);
    const secs = buildSections(data.tryout);
    setActive({ ...data.tryout, _pilihan: pilihanArr || [] });
    setSections(secs); setSecIdx(0); setQIdx(0); setAnswers({}); setResult(null);
    setTimeLeft((secs[0]?.durasi || 30) * 60);
    setPicker(null); setChosen([]);
  };

  const start = (item) => {
    if (item.kategori === "UTBK" && prodiTargets.length === 0) {
      toast.info("Pilih dulu target prodi (maks 4) sebelum TryOut UTBK.");
      setTargetModal(true); return;
    }
    if (item.pilihan_subtes && item.pilihan_subtes.length) { setPicker(item); setChosen([]); return; }
    begin(item.id);
  };

  const sec = sections[secIdx];
  const q = sec?.questions?.[qIdx];
  const secTotal = sec?.questions?.length || 0;
  const allTotal = active?.soal?.length || 0;
  const isLastSection = secIdx >= sections.length - 1;

  const submit = useCallback(async (auto = false) => {
    if (!active) return;
    if (!auto) {
      const kosong = allTotal - Object.keys(answers).length;
      if (kosong > 0 && !window.confirm(`Masih ada ${kosong} soal yang belum dijawab. Soal kosong dihitung salah. Tetap kumpulkan?`)) return;
    }
    setSubmitting(true);
    try {
      const qsp = active._pilihan && active._pilihan.length ? `?pilihan=${encodeURIComponent(active._pilihan.join(","))}` : "";
      const payload = { answers: active.soal.map((qq) => ({ id: qq.id, value: answers[qq.id] ?? -1 })) };
      const { data } = await api.post(`/tryout/${active.id}/submit${qsp}`, payload);
      setResult(data); loadList();
      toast.success(auto ? "Waktu subtes habis — dikumpulkan otomatis" : "TryOut selesai!");
    } catch { toast.error("Gagal mengirim"); }
    finally { setSubmitting(false); }
  }, [active, answers, allTotal]);

  const nextSection = useCallback(() => {
    if (isLastSection) { submit(true); return; }
    const ni = secIdx + 1;
    setSecIdx(ni); setQIdx(0);
    setTimeLeft((sections[ni]?.durasi || 30) * 60);
    toast.info(`Lanjut ke subtes: ${sections[ni]?.nama || ""}`);
  }, [isLastSection, secIdx, sections, submit]);

  useEffect(() => {
    if (!active || result || submitting) return;
    if (timeLeft <= 0) { nextSection(); return; }
    const tmr = setInterval(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearInterval(tmr);
  }, [active, result, submitting, timeLeft, nextSection]);

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const reset = () => { setActive(null); setResult(null); setAnswers({}); setSections([]); setSecIdx(0); setQIdx(0); setTimeLeft(0); };

  // Result view
  if (result) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto text-center py-6">
          <span className="inline-grid place-items-center h-20 w-20 rounded-2xl bg-[#4A6B53] text-white mb-5"><Trophy className="h-10 w-10" /></span>
          <h1 className="font-heading font-black text-3xl text-[#1A1C1A]">{result.nama}</h1>
          <p className="text-[#5C5F5C] mt-1">Skor kamu</p>
          <div className="font-heading font-black text-6xl text-[#E25E3E] my-3">{result.skor}</div>
          <p className="text-[#5C5F5C]">{result.benar} dari {result.total} jawaban benar</p>
          {result.skor_mentah != null && result.skor_mentah !== result.skor && (
            <p className="text-xs text-[#5C5F5C] mt-1" data-testid="skor-bobot-note">Skor berbobot <b>IRT-ringan</b> (soal sulit bernilai lebih) · skor mentah: {result.skor_mentah}</p>
          )}
          <p className="text-xs text-[#5C5F5C] mt-1" data-testid="utbk-scale-note">≈ <b>{Math.round(200 + (result.skor / 100) * 800)}</b> skala UTBK (estimasi 200–1000)</p>

          <MotivasiQuote skor={result.skor} className="mt-6" />

          <div className="clay-card p-6 mt-8 text-left">
            <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-4">Rincian per Subtes</h3>
            <div className="space-y-3">
              {result.subtes.map((st) => (
                <div key={st.subtes}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-[#1A1C1A] font-medium">{st.subtes}</span><span className="text-[#5C5F5C]">{st.benar}/{st.total}</span></div>
                  <div className="h-2.5 rounded-full bg-[#F0ECE4] overflow-hidden"><div className="h-full rounded-full bg-[#4A6B53]" style={{ width: `${st.skor}%` }} /></div>
                </div>
              ))}
            </div>
          </div>

          {result.kategori === "UTBK" && prodiTargets.length > 0 && (
            <div className="clay-card p-6 mt-6 text-left" data-testid="utbk-target-status">
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-1">Status Target Prodi</h3>
              <p className="text-xs text-[#5C5F5C] mb-4">Skor TryOut ini: <b className="text-[#4A6B53]">{result.skor}</b>. Estimasi keketatan — <b>bukan</b> kelulusan resmi SNPMB.</p>
              <div className="space-y-2">
                {prodiTargets.map((t, i) => {
                  const done = result.skor >= t.target;
                  return (
                    <div key={i} data-testid={`result-target-${i}`} className={`rounded-xl border p-3 flex items-center justify-between ${done ? "border-[#4A6B53]/40 bg-[#4A6B53]/5" : "border-[#D9381E]/30 bg-[#D9381E]/5"}`}>
                      <div className="min-w-0"><p className="text-sm font-medium text-[#1A1C1A] truncate">{t.prodi}</p><p className="text-xs text-[#5C5F5C] truncate">{t.ptn} · target ±{t.target}</p></div>
                      {done ? <span className="text-xs font-medium text-[#4A6B53] flex items-center gap-1 shrink-0"><CheckCircle2 className="h-4 w-4" /> Tercapai</span>
                        : <span className="text-xs font-medium text-[#D9381E] flex items-center gap-1 shrink-0"><XCircle className="h-4 w-4" /> Belum</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {result.analisis?.target && (() => {
            const tr = result.analisis.target;
            const theme = {
              tercapai: { bg: "bg-[#4A6B53]/10", br: "border-[#4A6B53]/40", ic: "text-[#4A6B53]", Icon: Trophy },
              dekat: { bg: "bg-[#F4B942]/10", br: "border-[#F4B942]/40", ic: "text-[#B8860B]", Icon: TrendingUp },
              jauh: { bg: "bg-[#E25E3E]/10", br: "border-[#E25E3E]/40", ic: "text-[#E25E3E]", Icon: Flag },
              belum: { bg: "bg-[#F0ECE4]", br: "border-[#E5E0D8]", ic: "text-[#5C5F5C]", Icon: Target },
            }[tr.status] || { bg: "bg-[#F0ECE4]", br: "border-[#E5E0D8]", ic: "text-[#5C5F5C]", Icon: Target };
            const Ic = theme.Icon;
            return (
              <div className={`clay-card p-6 mt-6 text-left border ${theme.br} ${theme.bg}`} data-testid="target-fit-rekomendasi">
                <div className="flex items-center gap-2 mb-2">
                  <Ic className={`h-5 w-5 ${theme.ic}`} />
                  <h3 className="font-heading font-bold text-lg text-[#1A1C1A]">{tr.judul}</h3>
                </div>
                <p className="text-sm text-[#5C5F5C] leading-relaxed">{tr.pesan}</p>
                {(tr.aksi === "sesuaikan" || tr.aksi === "set_target") && (
                  <button onClick={() => setTargetModal(true)} data-testid="atur-target-btn"
                    className="mt-4 px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center gap-2">
                    <Target className="h-4 w-4" /> {tr.aksi === "set_target" ? "Tetapkan Target Prodi" : "Sesuaikan Target Prodi"}
                  </button>
                )}
              </div>
            );
          })()}

          {result.analisis && (result.analisis.lemah?.length > 0 || result.analisis.saran?.length > 0) && (
            <div className="clay-card p-6 mt-6 text-left" data-testid="analisis-kelemahan">
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-1">Analisis &amp; Rekomendasi Belajar</h3>
              <p className="text-xs text-[#5C5F5C] mb-4">Berdasarkan performamu per subtes & tingkat kesulitan.</p>
              {result.analisis.per_tingkat && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {["mudah", "sedang", "sulit"].map((tk) => {
                    const d = result.analisis.per_tingkat[tk] || {}; const lbl = { mudah: "Mudah", sedang: "Sedang", sulit: "Sulit" }[tk]; const col = { mudah: "#4A6B53", sedang: "#B8860B", sulit: "#D9381E" }[tk];
                    return (
                      <div key={tk} className="rounded-xl border border-[#E5E0D8] p-3 text-center">
                        <p className="text-xs text-[#5C5F5C]">{lbl}</p>
                        <p className="font-heading font-black text-xl" style={{ color: col }}>{d.persen == null ? "—" : `${d.persen}%`}</p>
                        <p className="text-[10px] text-[#5C5F5C]">{d.benar || 0}/{d.total || 0} benar</p>
                      </div>
                    );
                  })}
                </div>
              )}
              {result.analisis.lemah?.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-semibold text-[#1A1C1A] mb-1">Area yang perlu diperkuat:</p>
                  <ul className="space-y-1">
                    {result.analisis.lemah.map((l, i) => (<li key={i} className="text-sm text-[#5C5F5C]">• <b className="text-[#D9381E]">{l.subtes}</b> (skor {l.skor}){l.detail ? ` — ${l.detail}` : ""}</li>))}
                  </ul>
                </div>
              )}
              {result.analisis.saran?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-[#1A1C1A] mb-1">Rekomendasi belajar:</p>
                  <ul className="space-y-1">{result.analisis.saran.map((s, i) => (<li key={i} className="text-sm text-[#4A6B53]">✓ {s}</li>))}</ul>
                </div>
              )}
            </div>
          )}

          {result.review && result.review.length > 0 && (
            <div className="clay-card p-6 mt-6 text-left" data-testid="tryout-review">
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-4">Pembahasan Soal</h3>
              <div className="space-y-4">
                {result.review.map((r, i) => (
                  <div key={i} className={`rounded-xl border p-4 ${r.benar ? "border-[#4A6B53]/40 bg-[#4A6B53]/5" : "border-[#D9381E]/30 bg-[#D9381E]/5"}`}>
                    <div className="flex items-start gap-2 mb-2">
                      {r.benar ? <CheckCircle2 className="h-5 w-5 text-[#4A6B53] shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-[#D9381E] shrink-0 mt-0.5" />}
                      <p className="text-sm font-medium text-[#1A1C1A]">{i + 1}. <MathText>{r.question}</MathText></p>
                    </div>
                    <div className="pl-7 space-y-1 text-sm">
                      <p className="text-[#5C5F5C]">Jawabanmu: <span className={r.benar ? "text-[#4A6B53] font-medium" : "text-[#D9381E] font-medium"}><MathText>{r.pilihanmu}</MathText></span></p>
                      {!r.benar && <p className="text-[#5C5F5C]">Jawaban benar: <span className="text-[#4A6B53] font-medium"><MathText>{r.jawaban_benar}</MathText></span></p>}
                      {r.pembahasan && <p className="text-[#1A1C1A] bg-white rounded-lg px-3 py-2 border border-[#E5E0D8]"><span className="font-semibold">Pembahasan:</span> <MathText>{r.pembahasan}</MathText></p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {active && <button onClick={() => begin(active.id, active._pilihan)} data-testid="retry-tryout-btn" className="px-6 py-3 rounded-full bg-[#1A1C1A] text-white font-medium hover:opacity-90 transition-opacity flex items-center gap-2"><RotateCcw className="h-4 w-4" /> Ulangi</button>}
            <button onClick={() => downloadFile(`/tryout/result/${result.id}/pdf`, `TryOut-${(result.nama || "Hasil").replace(/[^a-zA-Z0-9]+/g, "-")}.pdf`)} data-testid="download-tryout-pdf" className="px-6 py-3 rounded-full bg-[#4A6B53] text-white font-medium hover:opacity-90 transition-opacity flex items-center gap-2"><FileText className="h-4 w-4" /> Unduh PDF</button>
            <button onClick={reset} className="px-6 py-3 rounded-full border-2 border-[#1A1C1A] font-medium hover:bg-[#1A1C1A]/5 transition-colors">Kembali ke Daftar</button>
          </div>
        </div>
      </AppShell>
    );
  }

  // Taking test
  if (active && q) {
    const answered = answers[q.id] !== undefined;
    const isLastQ = qIdx >= secTotal - 1;
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <button onClick={reset} className="text-sm text-[#5C5F5C] flex items-center gap-1.5 hover:text-[#1A1C1A]"><ArrowLeft className="h-4 w-4" /> Keluar</button>
            <div className="flex items-center gap-3">
              <span data-testid="tryout-timer" className={`text-sm font-mono font-bold px-3 py-1 rounded-full flex items-center gap-1.5 ${timeLeft <= 60 ? "bg-[#D9381E]/10 text-[#D9381E]" : "bg-[#1A1C1A]/5 text-[#1A1C1A]"}`}>
                <Clock className="h-4 w-4" /><span>{mmss(timeLeft)}</span>
              </span>
              <span className="text-sm text-[#5C5F5C]">{qIdx + 1} / {secTotal}</span>
            </div>
          </div>
          {sec.nama && (
            <div className="flex items-center justify-between mb-2">
              <span data-testid="subtes-label" className="text-sm font-bold text-[#E25E3E]">{sec.nama}</span>
              <span className="text-xs text-[#5C5F5C]">Subtes {secIdx + 1}/{sections.length}</span>
            </div>
          )}
          <div className="h-2 rounded-full bg-[#F0ECE4] overflow-hidden mb-6"><div className="h-full bg-[#E25E3E] rounded-full transition-all" style={{ width: `${((qIdx + 1) / secTotal) * 100}%` }} /></div>

          <div className="clay-card p-7" data-testid="tryout-question">
            <div className="flex items-center gap-2 flex-wrap">
              {sec.nama && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#E25E3E]/10 text-[#E25E3E]">{q.subtes}</span>}
              {(() => { const tk = q.tingkat || "sedang"; const map = { sulit: ["Sulit", "bg-[#D9381E]/10 text-[#D9381E]"], mudah: ["Mudah", "bg-[#4A6B53]/10 text-[#4A6B53]"], sedang: ["Sedang", "bg-[#F4B942]/20 text-[#B8860B]"] }; const [lbl, cls] = map[tk] || map.sedang; return <span data-testid="tingkat-badge" className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{lbl}</span>; })()}
            </div>
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#1A1C1A] my-4 leading-snug"><MathText>{q.question}</MathText></h2>
            <div className="space-y-2.5">
              {q.options.map((opt, i) => (
                <button key={i} onClick={() => { setAnswers((a) => ({ ...a, [q.id]: i })); setTimeout(() => qIdx < secTotal - 1 && setQIdx((x) => x + 1), 180); }}
                  data-testid={`tryout-option-${i}`}
                  className={`w-full text-left px-4 py-3 rounded-xl border flex items-start gap-3 transition-colors ${answers[q.id] === i ? "border-[#E25E3E] bg-[#E25E3E]/10" : "border-[#E5E0D8] hover:border-[#E25E3E]/50"}`}>
                  <span className={`h-7 w-7 mt-0.5 rounded-full grid place-items-center text-sm font-bold shrink-0 ${answers[q.id] === i ? "bg-[#E25E3E] text-white" : "bg-[#F0ECE4] text-[#5C5F5C]"}`}>{String.fromCharCode(65 + i)}</span>
                  <span className="text-[#1A1C1A] leading-snug"><MathText>{opt}</MathText></span>
                </button>
              ))}
            </div>
            <button onClick={() => { setReportFor(q); setReportReason(""); }} data-testid="report-question-btn" className="mt-5 text-xs text-[#5C5F5C] hover:text-[#D9381E] inline-flex items-center gap-1.5 transition-colors"><Flag className="h-3.5 w-3.5" /> Laporkan soal (typo/error)</button>
          </div>

          <div className="flex items-center justify-between mt-6">
            <button onClick={() => setQIdx((i) => Math.max(0, i - 1))} disabled={qIdx === 0} className="px-4 py-2.5 rounded-full border-2 border-[#1A1C1A] text-sm font-medium disabled:opacity-30 flex items-center gap-1.5 hover:bg-[#1A1C1A]/5 transition-colors"><ArrowLeft className="h-4 w-4" /> Sebelumnya</button>
            {!isLastQ ? (
              <button onClick={() => setQIdx((i) => i + 1)} disabled={!answered} className="px-5 py-2.5 rounded-full bg-[#1A1C1A] text-white text-sm font-medium disabled:opacity-40 flex items-center gap-1.5 hover:opacity-90 transition-opacity">Berikutnya <ArrowRight className="h-4 w-4" /></button>
            ) : !isLastSection ? (
              <button onClick={nextSection} data-testid="next-subtes-btn" className="px-5 py-2.5 rounded-full bg-[#4A6B53] text-white text-sm font-medium flex items-center gap-1.5 hover:opacity-90 transition-opacity">Selesai Subtes → Lanjut <ArrowRight className="h-4 w-4" /></button>
            ) : (
              <button onClick={() => submit(false)} disabled={submitting} data-testid="submit-tryout-btn" className="px-6 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium disabled:opacity-60 flex items-center gap-1.5 hover:bg-[#C94F31] transition-colors">{submitting ? "Menilai…" : "Selesai"} <CheckCircle2 className="h-4 w-4" /></button>
            )}
          </div>
        </div>
        {reportFor && (
          <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="report-modal" onClick={() => !reportSending && setReportFor(null)}>
            <div className="clay-card p-6 max-w-md w-full bg-white" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mb-1 flex items-center gap-2"><Flag className="h-5 w-5 text-[#D9381E]" /> Laporkan Soal</h3>
              <p className="text-xs text-[#5C5F5C] mb-3">Jelaskan masalahnya (mis. salah ketik, rumus keliru, atau kunci jawaban salah). Laporan langsung dikirim ke admin.</p>
              <textarea value={reportReason} onChange={(e) => setReportReason(e.target.value)} rows={4} data-testid="report-reason-input" placeholder="Tuliskan masalah pada soal ini…" className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm" />
              <div className="flex gap-2 mt-4">
                <button onClick={submitReport} disabled={reportSending} data-testid="report-submit-btn" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] disabled:opacity-60 flex items-center gap-1.5"><Flag className="h-4 w-4" /> {reportSending ? "Mengirim…" : "Kirim Laporan"}</button>
                <button onClick={() => setReportFor(null)} disabled={reportSending} className="px-5 py-2.5 rounded-full border-2 border-[#1A1C1A] text-sm font-medium hover:bg-[#1A1C1A]/5">Batal</button>
              </div>
            </div>
          </div>
        )}
      </AppShell>
    );
  }

  // Mapel pilihan picker (TKA SMA)
  if (picker) {
    const toggle = (m) => setChosen((c) => c.includes(m) ? c.filter((x) => x !== m) : (c.length < (picker.pilihan_max || 2) ? [...c, m] : c));
    return (
      <AppShell>
        <div className="max-w-xl mx-auto py-6">
          <button onClick={() => { setPicker(null); setChosen([]); }} className="text-sm text-[#5C5F5C] flex items-center gap-1.5 mb-4 hover:text-[#1A1C1A]"><ArrowLeft className="h-4 w-4" /> Batal</button>
          <h1 className="font-heading font-black text-2xl text-[#1A1C1A] mb-1">{picker.nama}</h1>
          <p className="text-sm text-[#5C5F5C] mb-2">Mapel wajib: {picker.wajib_subtes?.join(", ")}.</p>
          <p className="text-[#1A1C1A] font-medium mb-4">Pilih maksimal {picker.pilihan_max || 2} mapel pilihan ({chosen.length} dipilih):</p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {picker.pilihan_subtes.map((m) => {
              const on = chosen.includes(m);
              return (
                <button key={m} onClick={() => toggle(m)} data-testid={`pilih-mapel-${m}`}
                  className={`py-3 rounded-xl border text-sm font-medium transition-colors ${on ? "border-[#E25E3E] bg-[#E25E3E]/10 text-[#E25E3E]" : "border-[#E5E0D8] text-[#5C5F5C] hover:border-[#E25E3E]/50"}`}>{m}</button>
              );
            })}
          </div>
          <button onClick={() => begin(picker.id, chosen)} disabled={chosen.length === 0} data-testid="mulai-tka-sma-btn"
            className="w-full py-3.5 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">Mulai TryOut <ArrowRight className="h-4 w-4" /></button>
        </div>
      </AppShell>
    );
  }

  // List view
  return (
    <AppShell>
      <div className="mb-8 max-w-2xl">
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-[#1A1C1A]">TryOut TKA & UTBK</h1>
        <p className="text-[#5C5F5C] mt-2">Latih kesiapanmu dan ukur posisimu. Hasil setiap percobaan tersimpan otomatis.</p>
      </div>

      {/* ===== Grafik Pencapaian: vs target & vs hasil sebelumnya ===== */}
      {history.length > 0 && (() => {
        const sorted = [...history].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        const utbk = sorted.filter((h) => h.kategori === "UTBK");
        const bestUTBK = utbk.length ? Math.max(...utbk.map((h) => h.skor)) : null;
        const primary = prodiTargets[0] || null;
        const last = sorted[sorted.length - 1];
        const prev = sorted.length >= 2 ? sorted[sorted.length - 2] : null;
        const rawDelta = prev ? last.skor - prev.skor : null;
        const delta = rawDelta == null ? null : Math.sign(rawDelta) * Math.round((Math.abs(rawDelta) + 1e-9) * 10) / 10;
        return (
          <div className="clay-card p-7 mb-8" data-testid="pencapaian-card">
            <div className="flex items-center gap-2 mb-1"><TrendingUp className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-black text-2xl text-[#1A1C1A]">Grafik Pencapaianmu</h2></div>
            <p className="text-sm text-[#5C5F5C] mb-5">Perbandingan skormu dengan <b>target</b> dan <b>hasil sebelumnya</b> — bukan peringkat dengan siswa lain.</p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="rounded-xl border border-[#E5E0D8] p-4">
                <p className="text-xs text-[#5C5F5C] mb-1">Skor terakhir</p>
                <p className="font-heading font-black text-2xl text-[#4A6B53]">{last.skor}</p>
                <p className="text-[11px] text-[#5C5F5C] truncate">{last.nama}</p>
              </div>
              <div className="rounded-xl border border-[#E5E0D8] p-4" data-testid="delta-card">
                <p className="text-xs text-[#5C5F5C] mb-1">vs hasil sebelumnya</p>
                {delta == null ? <p className="font-heading font-black text-2xl text-[#5C5F5C]">—</p>
                  : <p className={`font-heading font-black text-2xl flex items-center gap-1 ${delta > 0 ? "text-[#4A6B53]" : delta < 0 ? "text-[#D9381E]" : "text-[#5C5F5C]"}`}>
                      {delta > 0 ? <TrendingUp className="h-5 w-5" /> : delta < 0 ? <TrendingDown className="h-5 w-5" /> : <Minus className="h-5 w-5" />}
                      {delta > 0 ? `+${delta}` : delta}
                    </p>}
                <p className="text-[11px] text-[#5C5F5C]">{prev ? "poin dibanding TryOut lalu" : "kerjakan lagi untuk perbandingan"}</p>
              </div>
              <div className="rounded-xl border border-[#E5E0D8] p-4" data-testid="target-summary-card">
                <p className="text-xs text-[#5C5F5C] mb-1 flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Target utama (UTBK)</p>
                {primary ? <>
                  <p className="font-heading font-black text-lg text-[#1A1C1A] truncate">{primary.prodi}</p>
                  <p className="text-[11px] text-[#5C5F5C] truncate">{primary.ptn} · target ±{primary.target}{bestUTBK != null ? ` · terbaikmu ${bestUTBK}` : ""}</p>
                </> : <p className="text-sm text-[#5C5F5C]">Belum ada target prodi</p>}
              </div>
            </div>

            <ScoreTrendChart data={sorted} height={240} targetLine={primary?.target} targetLabel={primary ? `Target: ${primary.prodi}` : null} />

            {prodiTargets.length > 0 && bestUTBK != null && (
              <div className="mt-5 pt-5 border-t border-[#E5E0D8]">
                <p className="text-sm font-semibold text-[#1A1C1A] mb-3">Status target vs skor UTBK terbaikmu ({bestUTBK})</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {prodiTargets.map((t, i) => {
                    const done = bestUTBK >= t.target;
                    return (
                      <div key={i} data-testid={`pencapaian-target-${i}`} className={`rounded-xl border p-3 flex items-center justify-between ${done ? "border-[#4A6B53]/40 bg-[#4A6B53]/5" : "border-[#E5E0D8]"}`}>
                        <div className="min-w-0"><p className="text-sm font-medium text-[#1A1C1A] truncate">{t.prodi}</p><p className="text-xs text-[#5C5F5C] truncate">{t.ptn} · ±{t.target}</p></div>
                        {done ? <span className="text-xs font-medium text-[#4A6B53] flex items-center gap-1 shrink-0"><CheckCircle2 className="h-4 w-4" /> Tercapai</span>
                          : <span className="text-xs font-medium text-[#D9381E] flex items-center gap-1 shrink-0"><XCircle className="h-4 w-4" /> Belum</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="clay-card p-5 mb-6 flex items-start gap-3 bg-[#4A6B53]/10 border-[#4A6B53]/30" data-testid="tryout-info-banner">
        <Crown className="h-5 w-5 text-[#4A6B53] shrink-0 mt-0.5" />
        <p className="text-sm text-[#1A1C1A]">TryOut <b>Coba Gratis</b> bisa langsung dikerjakan. TryOut <b>Premium</b> (standar TKA nasional & SNPMB) dibuka dengan membeli 1x akses atau paket hemat 5x/tahun.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {list.map((t) => (
          <div key={t.id} className="clay-card clay-card-hover p-6" data-testid={`tryout-${t.id}`}>
            <div className="flex items-center justify-between mb-3">
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${t.kategori === "UTBK" ? "bg-[#4A6B53] text-white" : "bg-[#F4B942] text-[#1A1C1A]"}`}>{t.kategori}</span>
              <div className="flex items-center gap-2">
                {t.premium && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#1A1C1A] text-white flex items-center gap-1"><Crown className="h-3 w-3" /> Premium</span>}
                {t.premium && !t.terkunci && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#4A6B53] text-white" data-testid={`akses-badge-${t.id}`}>Sisa {t.sisa_akses}x</span>}
                {t.skor_terbaik != null && <span className="text-sm text-[#5C5F5C]">Terbaik: <b className="text-[#E25E3E]">{t.skor_terbaik}</b></span>}
              </div>
            </div>
            <h3 className="font-heading font-bold text-xl text-[#1A1C1A]">{t.nama}</h3>
            <p className="text-sm text-[#5C5F5C] mt-1 mb-4">{t.deskripsi}</p>
            {t.premium && !t.terkunci && t.akses_expiry && (
              <p className="text-xs text-[#4A6B53] -mt-3 mb-4" data-testid={`akses-expiry-${t.id}`}>Paket aktif · berlaku s/d {new Date(t.akses_expiry).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}</p>
            )}
            <div className="flex items-center gap-4 text-sm text-[#5C5F5C] mb-5">
              <span className="flex items-center gap-1.5"><FileText className="h-4 w-4" /> {t.jumlah_soal} soal</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {t.durasi_menit} menit</span>
            </div>
            {t.premium && t.terkunci ? (
              <div className="space-y-2" data-testid={`buy-tryout-${t.id}`}>
                <button onClick={() => buyTryout(t.id, "single")} disabled={buying === `${t.id}:single`} data-testid={`buy-single-${t.id}`} className="w-full py-3 rounded-full bg-[#1A1C1A] text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60">
                  Beli 1x — {rupiah(t.harga_single)} <Lock className="h-4 w-4" />
                </button>
                <button onClick={() => buyTryout(t.id, "bundle")} disabled={buying === `${t.id}:bundle`} data-testid={`buy-bundle-${t.id}`} className="w-full py-2.5 rounded-full border-2 border-[#E25E3E] text-[#E25E3E] font-medium hover:bg-[#E25E3E]/5 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  Paket {t.bundle_akses}x / tahun — {rupiah(t.harga_bundle)}
                </button>
              </div>
            ) : (
              <button onClick={() => start(t)} data-testid={`start-tryout-${t.id}`} className="w-full py-3 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors flex items-center justify-center gap-2">
                {t.premium ? `Mulai (sisa ${t.sisa_akses} akses)` : t.pilihan_subtes ? "Pilih Mapel & Mulai" : "Mulai TryOut"} <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {eksternal.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-1">
            <ExternalLink className="h-5 w-5 text-[#E25E3E]" />
            <h2 className="font-heading font-black text-2xl text-[#1A1C1A]">TryOut Bimbel Talenta (Premium)</h2>
          </div>
          <p className="text-[#5C5F5C] mb-5 text-sm">Paket TryOut berbayar dari Bimbel Talenta. Membuka situs eksternal di tab baru.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {eksternal.map((t) => (
              <a key={t.id} href="https://bimbeltalenta.id/" target="_blank" rel="noopener noreferrer" data-testid={`ext-tryout-${t.id}`}
                className="clay-card clay-card-hover p-6 block border-dashed">
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${t.kategori === "UTBK" ? "bg-[#4A6B53] text-white" : "bg-[#F4B942] text-[#1A1C1A]"}`}>{t.kategori}</span>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#1A1C1A] text-white flex items-center gap-1"><Lock className="h-3 w-3" /> Berbayar</span>
                </div>
                <h3 className="font-heading font-bold text-xl text-[#1A1C1A]">{t.nama}</h3>
                <p className="text-sm text-[#5C5F5C] mt-1 mb-2">{t.deskripsi}</p>
                <p className="text-xs text-[#5C5F5C] mb-4">Penyedia: Bimbel Talenta</p>
                <span className="w-full py-3 rounded-full border-2 border-[#1A1C1A] text-[#1A1C1A] font-medium hover:bg-[#1A1C1A]/5 transition-colors flex items-center justify-center gap-2">Buka di Bimbel Talenta <ExternalLink className="h-4 w-4" /></span>
              </a>
            ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-10">
          <h2 className="font-heading font-black text-2xl text-[#1A1C1A] mb-1">Riwayat TryOut</h2>
          <p className="text-sm text-[#5C5F5C] mb-4">Ketuk salah satu untuk melihat skor, rincian per subtes, dan pembahasan soal.</p>
          <div className="clay-card divide-y divide-[#E5E0D8]">
            {history.map((h) => (
              <button key={h.id} onClick={() => setResult(h)} data-testid={`riwayat-item-${h.id}`}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-[#F0ECE4]/60 transition-colors">
                <div className="min-w-0">
                  <p className="font-medium text-[#1A1C1A] truncate">{h.nama}</p>
                  <p className="text-xs text-[#5C5F5C]">{new Date(h.created_at).toLocaleString("id-ID")}</p>
                  <span className="text-xs font-medium text-[#E25E3E] flex items-center gap-1 mt-0.5">Lihat pembahasan <ArrowRight className="h-3 w-3" /></span>
                </div>
                <span className="font-heading font-black text-xl text-[#4A6B53] shrink-0 ml-3">{h.skor}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <TalentaCTA className="mt-10" title="Mau skor TryOut lebih tinggi?" sub="Latihan intensif TKA/UTBK & pembahasan bareng mentor di Bimbel Talenta." />
      <TargetProdiModal open={targetModal} onClose={() => setTargetModal(false)} onSaved={() => api.get("/student/prodi").then((r) => setProdiTargets(r.data.items || [])).catch(() => {})} />
    </AppShell>
  );
}
